import { restamp, tombstoneOf, type AnyEnvelope, type HlcClock } from '@wiztime/core';
import { UndoConflictError, UndoConflictReason } from '../errors';
import type { HistoryGuard } from './history-guard';
import type { RepoWriter } from '../repo';
import type { UndoEntry, UndoStep } from './types';

export const StepDirection = {
  Undo: 'undo',
  Redo: 'redo',
} as const;
export type StepDirection = (typeof StepDirection)[keyof typeof StepDirection];

export interface ApplyEnvironment {
  readonly clock: HlcClock;
  readonly deviceId: string;
  readonly nowMs: number;
}

function imageOf(entry: UndoEntry, direction: StepDirection, env: ApplyEnvironment): AnyEnvelope {
  const hlc = env.clock.tick();
  if (direction === StepDirection.Redo) return restamp(entry.after, hlc, env.deviceId);
  return entry.before === null
    ? tombstoneOf(entry.after, hlc, env.deviceId)
    : restamp(entry.before, hlc, env.deviceId);
}

/**
 * Apply an undo step (write the before images) or a redo step (write the after images) as new
 * writes with fresh timestamps (newer than the stored records), inside the caller's
 * transaction. Aborts with an UndoConflictError, before writing anything, when a record changed
 * since the step wrote it ('remoteChange'). Blocks may overlap, so the restored records are
 * never checked against other blocks. Returns the timestamp written to each record.
 */
export async function applyStep(
  tx: RepoWriter,
  env: ApplyEnvironment,
  guard: HistoryGuard,
  step: UndoStep,
  direction: StepDirection,
): Promise<ReadonlyMap<string, string>> {
  const stored = await tx.getMany(step.entries.map((entry) => entry.id));
  const stale = guard.staleIds(step, stored);
  if (stale.length > 0) {
    throw new UndoConflictError(UndoConflictReason.RemoteChange, step.label, stale);
  }

  // The images must be newer than the stored records even if this store's clock lags behind them.
  for (const record of stored) if (record !== undefined) env.clock.receive(record.hlc);
  const images = step.entries.map((entry) => imageOf(entry, direction, env));
  await tx.putMany(images);
  return new Map(images.map((image) => [image.id, image.hlc]));
}
